const KEY = "academy.recent-searches";

/** Most recent searches the tray remembers. */
export const RECENT_SEARCH_LIMIT = 5;

const MAX_LENGTH = 100;

const storage = (): Storage | null => {
  try {
    return typeof window === "undefined" ? null : window.localStorage;
  } catch {
    return null;
  }
};

/**
 * The searches this browser made, newest first. Private windows and blocked
 * storage read as none.
 *
 * @returns Up to five search texts.
 */
export const readRecentSearches = (): string[] => {
  try {
    const raw: unknown = JSON.parse(storage()?.getItem(KEY) ?? "[]");

    return Array.isArray(raw)
      ? raw
          .filter((entry): entry is string => typeof entry === "string" && entry.trim() !== "")
          .slice(0, RECENT_SEARCH_LIMIT)
      : [];
  } catch {
    return [];
  }
};

const write = (entries: string[]) => {
  try {
    storage()?.setItem(KEY, JSON.stringify(entries));
  } catch {
    // Storage full or blocked: recent searches are a convenience only.
  }
};

/**
 * Remembers a search, moving it to the front if it was already there (ignoring
 * case) and keeping the newest five.
 *
 * @param q - The search text.
 * @returns The new list.
 */
export const saveRecentSearch = (q: string): string[] => {
  const text = q.trim().slice(0, MAX_LENGTH);

  if (text === "") {
    return readRecentSearches();
  }

  const next = [
    text,
    ...readRecentSearches().filter(
      (entry) => entry.toLocaleLowerCase() !== text.toLocaleLowerCase()
    ),
  ].slice(0, RECENT_SEARCH_LIMIT);

  write(next);

  return next;
};

/**
 * Forgets one search, or all of them when no text is given.
 *
 * @param q - The search to forget; omit to clear the list.
 * @returns The new list.
 */
export const forgetRecentSearch = (q?: string): string[] => {
  const next =
    q === undefined ? [] : readRecentSearches().filter((entry) => entry !== q);

  write(next);

  return next;
};
